import { Suspense } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { Section, SectionHeader } from "@roqua/components/sections";

import { LoadingSpinner } from "@/components/LoadingSpinner";
import { PageTitle } from "@/components/PageTitle";
import { SolicitFeedback } from "@/components/SolicitFeedback";
import { Choose } from "@/features/Measure/Choose";
import {
  RespondentTypeFilter,
  RespondentTypeFilterValue,
  respondentTypeFilterValue,
  TeamFilter,
  TypeFilter,
  TypeFilterValue,
  typeFilterValue,
} from "@/features/Measure/Choose/Filters";
import { SearchInput } from "@/features/Measure/Choose/SearchInput";
import { useTeam } from "@/hooks/useTeam";
import { useTranslation } from "@/i18n/client";

const searchSchema = z.object({
  type: typeFilterValue.default("all"),
  respondentType: respondentTypeFilterValue.default("all"),
  searchTerm: z.string().default(""),
});

export const Route = createFileRoute("/$organizationKey/$dossierId/measure")({
  validateSearch: zodValidator(searchSchema),
  component: Page,
});

function Page() {
  const { t } = useTranslation(["Measure", "common"]);
  const { searchTerm, type, respondentType } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { teamId, setTeamId } = useTeam();
  const setSearchTerm = (term: string) => {
    navigate({ search: (old) => ({ ...old, searchTerm: term }), replace: true });
  };
  const setType = (type: TypeFilterValue) => {
    navigate({ search: (old) => ({ ...old, type }), replace: true });
  };
  const setRespondentType = (respondentType: RespondentTypeFilterValue) => {
    navigate({ search: (old) => ({ ...old, respondentType }), replace: true });
  };
  const filters = { searchTerm, teamId, type, respondentType };

  return (
    <>
      <PageTitle title="measure" />
      <Section variant="header">
        <SectionHeader header={t("startNewMeasurement")} buttons={<SolicitFeedback />} />

        <div className="flex flex-col gap-2">
          <div className="text-foreground flex flex-col gap-2 md:flex-row">
            <TeamFilter onValueChange={(teamId) => setTeamId(teamId)} />
            <TypeFilter value={type} onValueChange={setType} />
            <RespondentTypeFilter value={respondentType} onValueChange={setRespondentType} />
          </div>

          <SearchInput
            defaultValue={searchTerm}
            onSearch={setSearchTerm}
            placeholder={t("searchTerm.placeholder")}
          />
        </div>
      </Section>
      <Section>
        <Suspense
          fallback={
            <div className="flex justify-center pt-8 text-lg">
              <LoadingSpinner />
            </div>
          }
        >
          <Choose filters={filters} />
        </Suspense>
      </Section>
    </>
  );
}
